import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";
import "../css/momDashboard.css";

const API_URL = process.env.REACT_APP_API_URL;
const REVIEW_OPTIONS = ["Not Reviewed", "State Lead Reviewed", "Zonal Reviewed"];
const PAGE_SIZE = 50;

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({ ...base, minHeight: 42, borderColor: "#cfd9e6" }),
};
const opt = (v) => ({ value: v, label: v });
const portal = typeof document !== "undefined" ? document.body : null;

const MeetingDashboard = () => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [zone, setZone] = useState(null);
  const [pc, setPc] = useState(null);
  const [ac, setAc] = useState(null);
  const [review, setReview] = useState(null);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [viewerLocation, setViewerLocation] = useState("");
  const isPunjab = viewerLocation === "Punjab";

  useEffect(() => {
    localforage.getItem("location").then((l) => setViewerLocation(l || ""));
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = await localforage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        // Build optional date-range params (loads that window from the server).
        const params = {};
        if (startDate) params.from = startDate;
        if (endDate) params.to = endDate;
        const qs = new URLSearchParams(params).toString();
        const suffix = qs ? `?${qs}` : "";

        const [listRes, sumRes] = await Promise.all([
          api.get(`${API_URL}/mom-format/all-meetings${suffix}`, { headers }),
          api.get(`${API_URL}/mom-format/meeting-summary${suffix}`, { headers }),
        ]);
        setRecords(Array.isArray(listRes.data) ? listRes.data : []);
        setSummary(
          sumRes.data && typeof sumRes.data.total === "number"
            ? sumRes.data
            : null
        );
      } catch (e) {
        console.error("Failed to load meetings", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [startDate, endDate]);

  // Build the Zone/Region -> PC/District -> AC cascade from the records the
  // viewer can actually see. Records are already state-scoped on the server, so
  // this guarantees a Punjab user only ever sees Punjab values, an AP user only
  // AP values, etc. — no cross-state leakage from a global geo tree.
  const recordTree = useMemo(() => {
    const t = {};
    records.forEach((r) => {
      // Punjab MoMs store geography in region/district; AP in zone/pc.
      const z = isPunjab ? r.region || r.zone : r.zone;
      const p = isPunjab ? r.district || r.pc : r.pc;
      const a = r.location;
      if (!z) return;
      t[z] = t[z] || {};
      if (p) {
        t[z][p] = t[z][p] || new Set();
        if (a) t[z][p].add(a);
      }
    });
    return t;
  }, [records, isPunjab]);

  const hasTree = Object.keys(recordTree).length > 0;

  const zoneOpts = useMemo(
    () => Object.keys(recordTree).sort().map(opt),
    [recordTree]
  );

  const pcOpts = useMemo(() => {
    if (!zone) return [];
    return Object.keys(recordTree[zone.value] || {}).sort().map(opt);
  }, [recordTree, zone]);

  const acOpts = useMemo(() => {
    if (!zone || !pc) return [];
    return Array.from((recordTree[zone.value] || {})[pc.value] || [])
      .sort()
      .map(opt);
  }, [recordTree, zone, pc]);

  // reset page + dependent filters
  useEffect(() => setPage(1), [zone, pc, ac, review, search, startDate, endDate]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      const rZone = isPunjab ? r.region || r.zone : r.zone;
      const rPc = isPunjab ? r.district || r.pc : r.pc;
      if (zone && rZone !== zone.value) return false;
      if (pc && rPc !== pc.value) return false;
      if (ac && r.location !== ac.value) return false;
      if (review && (r.reviewStatus || "Not Reviewed") !== review.value)
        return false;
      if (startDate && (r.meetingDate || "") < startDate) return false;
      if (endDate && (r.meetingDate || "") > endDate) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.respondentName || ""} ${r.respondentDesignation || ""} ${
          r.createdByName || ""
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [records, zone, pc, ac, review, search, startDate, endDate]);

  // Accurate counts. When the only active scope is the date range (handled
  // server-side), show the TRUE totals from the summary endpoint. When the
  // user narrows further with client-side filters, show the filtered counts.
  const clientFiltered = zone || pc || ac || review || search;
  const totalMeetings =
    !clientFiltered && summary ? summary.total : filtered.length;
  const totalMom =
    !clientFiltered && summary
      ? summary.totalMom
      : filtered.filter((r) => r.momSubmitted).length;
  const zoneCount = zone
    ? records.filter((r) => (isPunjab ? r.region || r.zone : r.zone) === zone.value).length
    : 0;
  const pcCount = pc
    ? records.filter((r) => (isPunjab ? r.district || r.pc : r.pc) === pc.value).length
    : 0;
  const acCount = ac ? records.filter((r) => r.location === ac.value).length : 0;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleReviewChange = async (id, value) => {
    try {
      const token = await localforage.getItem("token");
      await api.patch(
        `${API_URL}/mom-format/${id}/review`,
        { reviewStatus: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecords((rows) =>
        rows.map((r) => (r._id === id ? { ...r, reviewStatus: value } : r))
      );
    } catch (e) {
      alert("Could not update review status.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this meeting record?")) return;
    try {
      const token = await localforage.getItem("token");
      await api.delete(`${API_URL}/mom-format/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords((rows) => rows.filter((r) => r._id !== id));
    } catch (e) {
      alert("Could not delete the record.");
    }
  };

  const resetFilters = () => {
    setZone(null);
    setPc(null);
    setAc(null);
    setReview(null);
    setSearch("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <>
      <Dashboard />
      <div className="md-page">
        <h2 className="md-title">Meeting Dashboard</h2>

        <div className="md-filters">
          <div className="md-field">
            <label>{isPunjab ? "Region" : "Zone"}</label>
            <Select
              options={zoneOpts}
              value={zone}
              onChange={(v) => {
                setZone(v);
                setPc(null);
                setAc(null);
              }}
              isClearable
              placeholder={isPunjab ? "Select Region" : "Select Zone"}
              menuPortalTarget={portal}
              styles={selectStyles}
            />
          </div>
          <div className="md-field">
            <label>{isPunjab ? "District" : "Parliament Constituency"}</label>
            <Select
              options={pcOpts}
              value={pc}
              onChange={(v) => {
                setPc(v);
                setAc(null);
              }}
              isClearable
              isDisabled={hasTree && !zone}
              placeholder={
                hasTree && !zone
                  ? isPunjab
                    ? "Select Region first"
                    : "Select Zone first"
                  : isPunjab
                  ? "Select District"
                  : "Select PC"
              }
              menuPortalTarget={portal}
              styles={selectStyles}
            />
          </div>
          <div className="md-field">
            <label>Assembly Constituency</label>
            <Select
              options={acOpts}
              value={ac}
              onChange={setAc}
              isClearable
              isDisabled={hasTree && !pc}
              placeholder={
                hasTree && !pc
                  ? isPunjab
                    ? "Select District first"
                    : "Select PC first"
                  : "Select AC"
              }
              menuPortalTarget={portal}
              styles={selectStyles}
            />
          </div>
          <div className="md-field">
            <label>MoM Review</label>
            <Select
              options={REVIEW_OPTIONS.map(opt)}
              value={review}
              onChange={setReview}
              isClearable
              placeholder="All"
              menuPortalTarget={portal}
              styles={selectStyles}
            />
          </div>
          <div className="md-field">
            <label>Search (Name or Designation)</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
            />
          </div>
          <div className="md-field">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="md-field">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="md-field md-field-btn">
            <button className="md-reset" onClick={resetFilters} type="button">
              Reset Filters
            </button>
          </div>
        </div>

        <div className="md-counts">
          <span>
            Total Meeting Count: <b>{totalMeetings}</b>
          </span>
          <span>
            Zone-wise: <b>{zoneCount}</b>
          </span>
          <span>
            PC-wise: <b>{pcCount}</b>
          </span>
          <span>
            AC-wise: <b>{acCount}</b>
          </span>
          <span>
            Total MoM Count: <b>{totalMom}</b>
          </span>
        </div>

        <div className="md-table-wrap">
          <table className="md-table">
            <thead>
              <tr>
                <th>Created By</th>
                <th>Recorded Date &amp; Time</th>
                <th>Assembly Constituency</th>
                <th>Respondent Name</th>
                <th>Photo</th>
                <th>Designation</th>
                {isPunjab && <th>Party</th>}
                <th>Date of Meeting</th>
                <th>Meeting Location</th>
                <th>MoM Review Status</th>
                <th>Minutes of Meeting</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isPunjab ? 11 : 10} className="md-empty">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={isPunjab ? 11 : 10} className="md-empty">
                    No meetings found.
                  </td>
                </tr>
              ) : (
                pageRows.map((r) => {
                  const isFormat = r.source === "format";
                  const viewPath = isFormat
                    ? `/mom-format-view/${r._id}`
                    : `/view-new-mom/${r._id}`;
                  const editPath = isFormat
                    ? `/mom-format-edit/${r._id}`
                    : `/update-new-mom/${r._id}`;
                  return (
                    <tr key={`${r.source}-${r._id}`}>
                      <td>{r.createdByName || "—"}</td>
                      <td>
                        {r.createdAt ? new Date(r.createdAt).toLocaleString() : "—"}
                      </td>
                      <td>{r.location || "—"}</td>
                      <td>{r.respondentName || "—"}</td>
                      <td>
                        <button
                          type="button"
                          className="md-photo-view"
                          style={{
                            background: "none",
                            border: "1px solid #1e74d6",
                            color: "#1e74d6",
                            borderRadius: "6px",
                            padding: "3px 10px",
                            fontSize: "12px",
                            cursor: "pointer",
                          }}
                          onClick={async () => {
                            try {
                              const { data } = await api.get(
                                `${API_URL}/mom-format/meeting-photo/${r._id}?source=${r.source}`
                              );
                              if (data && data.photo) {
                                const w = window.open();
                                if (w)
                                  w.document.write(
                                    `<img src="${data.photo}" style="max-width:100%" alt="respondent" />`
                                  );
                              } else {
                                alert("No photo available for this meeting.");
                              }
                            } catch (e) {
                              alert("Could not load photo.");
                            }
                          }}
                        >
                          View
                        </button>
                      </td>
                      <td>{r.respondentDesignation || "—"}</td>
                      {isPunjab && <td>{r.respondentParty || "—"}</td>}
                      <td>
                        {r.meetingDate || "—"}
                        {r.meetingTime ? `, ${r.meetingTime}` : ""}
                      </td>
                      <td>
                        {r.gMapLocation ? (
                          <a
                            href={`https://www.google.com/maps?q=${r.gMapLocation}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View on Google Maps
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {isFormat ? (
                          <select
                            className="md-review"
                            value={r.reviewStatus || "Not Reviewed"}
                            onChange={(e) =>
                              handleReviewChange(r._id, e.target.value)
                            }
                          >
                            {REVIEW_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span>{r.reviewStatus || "Not Reviewed"}</span>
                        )}
                      </td>
                      <td className="md-actions">
                        <Link to={viewPath}>View</Link>
                        <Link to={editPath}>Update</Link>
                        {isFormat && (
                          <button
                            type="button"
                            className="md-del"
                            onClick={() => handleDelete(r._id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="md-pagination">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages} ({filtered.length} records)
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MeetingDashboard;
